import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DynamicObjectInterface } from '../../../../sdk/type';
import {
  AdminPageHeader,
  ButtonStyles,
  Input,
  InputTypes,
  MLPVersion,
  Theme,
  RadioButton,
  PhoneNumber,
  Status,
  InputValidations,
  SearchComponent,
  DropDown,
} from '../../../../ui/components';
import { LoggerService } from '../../../../ui/utils';
import { DropDownData } from '../../../../ui/components/Form/DropDown/types/propsType';
import { useAdminToast } from '../../../../ui/components/AdminToast/AdminToast';
import { countryCodeNumber } from '../../../../__mockData/phoneNumber';
import { formatCampaignId, formatPhoneNumberForMDMS, isValidEmail } from '../../../../ui/utils/common';
import { useAPICall } from '../../../../hooks';
import CoachService from '../../../../sdk/services/coachService';
import { SearchedUserData } from '../../teams/teamsListConfig';
import { User } from '../../teams/edit/add-participant/[teamId]';
import { DropdownOption } from '../../../../ui/components/Form/SearchComponent/types/propTypes';
import ParticipantService from '../../../../sdk/services/participantService';
import userService from '../../../../sdk/services/userService';
import MDMSService from '../../../../sdk/services/mdmsService';
import CampaignService from '../../../../sdk/services/campaignService';
import styles from './index.module.scss';

export interface CoachData {
  recId: number;
  coachID: string;
  aboID: string;
  name: string;
  contact: string;
  email: string;
  remark: string;
  status: string;
  [key: string]: string | number | undefined;
  pinLevel?: string;
  pin?: string;
  directLeaderId?: string;
  directLeaderName?: string;
  directLeaderPinLevel?: string;
  gender?: string;
  race?: string;
  address?: string;
  loaName?: string;
  accountType?: string;
  highestPinLevel?: string | number;
}

export const AdminCoachEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { adminToast } = useAdminToast();
  const id = searchParams.get('');
  const [coachData, setCoachData] = useState<CoachData | null>(null);
  const [status, setStatus] = useState('');
  const [invalid, setInvalid] = useState(true);
  const [phoneClicked, setPhoneClicked] = useState(false);
  const [emailValid, setEmailValid] = useState(true);
  const [phoneValue, setPhoneValue] = useState('');
  const [dropdownValue, setDropdownValue] = useState(countryCodeNumber[0].name);
  const [userList, setUserList] = useState<User[]>([]);
  const [mdmsDataUsed, setMDMSDataUsed] = useState(false);
  const [selectedUserIndex, setSelectedUserIndex] = useState<number>(0);
  const [userData, setUserData] = useState<SearchedUserData[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [campaignDropdownData, setCampaignDropdownData] = useState<DropDownData[]>([]);

  const apiCall = useAPICall();
  const isNewCoach = id === 'new';

  // Use useEffect to update coachData when id changes
  useEffect(() => {
    (async () => {
      if (!isNewCoach) {
        try {
          const response = await apiCall(CoachService.getCoachDetails, `${id}`);
          setCoachData(response?.data?.data ?? {});
        } catch (error) {
          LoggerService.log('Failed to retrieve coach details', error);
        }
      }

      const res = await apiCall(CampaignService?.getCampaigns);

      const campaignData =
        (res?.data?.data ?? [])
          .filter(
            (i: DynamicObjectInterface) =>
              i.status !== 'DELETED' &&
              i.status !== 'CLOSED' &&
              i.campaignType === 'CHALLENGE',
          )
          .sort(
            (a: DynamicObjectInterface, b: DynamicObjectInterface) =>
              new Date(b.campaignStart).getTime() -
              new Date(a.campaignStart).getTime(),
          );

      setCampaignDropdownData(
        campaignData.map((d: DynamicObjectInterface) => ({
          value: d.campaignId,
          title: `${d.campaignId} - ${d.campaignName}`,
        })),
      );
    })();
  }, [id, isNewCoach]);

  useEffect(() => {
    if (coachData) {
      const { contact } = coachData;
      if (contact) {
        const [countryCode, number] = contact.split(' ');
        setDropdownValue(countryCode);
        setPhoneValue(number);
      }
      setStatus(coachData.status);
    }
  }, [coachData]);

  const validateInputs = () => {
    if (isNewCoach) {
      // For new coach
      const { coachID, aboID, name } = coachData ?? {};
      const isAnyFieldEmpty = !coachID || !aboID || !name;

      setInvalid(isAnyFieldEmpty);
    } else {
      // For existing coach
      const { aboID, name } = coachData ?? {};
      const isAnyFieldEmpty = !aboID || !name;
      setInvalid(isAnyFieldEmpty);
    }
  };

  const handleInputChange = (name: string, value: DynamicObjectInterface) => {
    setCoachData(prevCoachData => ({
      ...prevCoachData!,
      [name]: value,
    }));
    if (name === 'email') {
      setEmailValid(isValidEmail(value as string));
    }

    validateInputs();
  };

  useEffect(() => {
    validateInputs();
  }, [coachData]);

  const onSave = async () => {
    const payload = {
      recId: coachData?.recId,
      coachID: coachData?.coachID,
      aboID: coachData?.aboID,
      campaignId: coachData?.campaignId,
      name: coachData?.name,
      contact: coachData?.contact,
      email: coachData?.email,
      remark: coachData?.remark,
      status: coachData?.status,
      createdBy: '',
      modifiedBy: '',
      pinLevel: coachData?.pinLevel,
      directLeader: coachData?.directLeaderId,
      directLeaderName: coachData?.directLeaderName,
      directLeaderPin: coachData?.directLeaderPinLevel,
      gender: coachData?.gender,
      race: coachData?.race,
      address: coachData?.address,
      loaName: coachData?.loaName,
      accountType: coachData?.accountType,
      highestPinLevel: coachData?.highestPinLevel,
      partyId: coachData?.partyId,
    };

    let result = isNewCoach
      ? await apiCall(CoachService.createCoach, payload)
      : await apiCall(CoachService.updateCoach, `${id}`, payload);

    if (result.status === 200 && result?.data?.status) {
      navigate(`/admin/coach-setup/list?campaignId=${coachData?.campaignId}`);
      adminToast({
        message: result?.data?.message,
        theme: Theme.SUCCESS,
      });
    } else {
      adminToast({
        message: result?.data?.message,
        theme: Theme.ERROR,
      });
    }
  };

  const buttonConfig = [
    {
      title: t('admin.coachEdit.cancel'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        navigate(`/admin/coach-setup/list`);
      },
    },
    {
      title: t('admin.coachEdit.save'),
      buttonStyle: ButtonStyles.SOLID,
      disabled: invalid,
      onClick: () => {
        if (formatCampaignId(coachData?.coachID ?? '')) {
          onSave();
        } else {
          if (!formatCampaignId(coachData?.coachID ?? '')) {
            adminToast({
              message: t('Only letters (A-Z), numbers, and underscores (_) are allowed in Campaign ID.'),
              theme: Theme.ERROR,
            });
          }
        }
      },
    },
  ];

  const searchUser = async (aboNumber: string) => {
    // If the search input is empty, show the full userList
    if (aboNumber?.length > 3) {
      //retrieve MDMS Data
      try {
        const result = await apiCall(MDMSService.getMDMSUserData, { aboNumber: aboNumber, pinCriteria: null });
        const mdmsListData = result?.data?.data;
        if (Object.keys(mdmsListData)?.length > 0) {
          // for the mdms list dropdown
          let mdmsUserList = [
            {
              id: mdmsListData?.aboNumber,
              title: mdmsListData?.primaryPartyDtl?.fullName,
              phone: formatPhoneNumberForMDMS(mdmsListData?.primaryPartyDtl?.phoneNumber),
              email: mdmsListData?.primaryPartyDtl?.email,
              pinLevel: mdmsListData?.pin,
              directLeaderName: mdmsListData?.sponsor?.sponsorName,
              directLeaderPinLevel: mdmsListData?.sponsor?.pin,
              directLeaderId: mdmsListData?.sponsor?.sponsorABONo,
              gender: mdmsListData?.primaryPartyDtl?.gender?.toUpperCase(),
              race: mdmsListData?.primaryPartyDtl?.race?.toUpperCase(),
              address: mdmsListData?.primaryPartyDtl?.address,
              loaName: mdmsListData?.loaName,
              accountType: mdmsListData?.accountType,
              highestPinLevel: mdmsListData?.highestPinLevel,
              partyId: mdmsListData?.primaryPartyDtl?.partyId,
            },
          ];
          // for the mdsm list for couple
          if (mdmsListData?.isCouple) {
            mdmsUserList.push({
              id: mdmsListData?.aboNumber,
              title: mdmsListData?.secondaryPartyDtl?.fullName,
              phone: formatPhoneNumberForMDMS(mdmsListData?.secondaryPartyDtl?.phoneNumber),
              email: mdmsListData?.secondaryPartyDtl?.email,
              pinLevel: mdmsListData?.pin,
              directLeaderName: mdmsListData?.sponsor?.sponsorName,
              directLeaderPinLevel: mdmsListData?.sponsor?.pin,
              directLeaderId: mdmsListData?.sponsor?.sponsorABONo,
              gender: mdmsListData?.secondaryPartyDtl?.gender?.toUpperCase(),
              race: mdmsListData?.secondaryPartyDtl?.race?.toUpperCase(),
              address: mdmsListData?.secondaryPartyDtl?.address,
              loaName: mdmsListData?.loaName,
              accountType: mdmsListData?.accountType,
              highestPinLevel: mdmsListData?.highestPinLevel,
              partyId: mdmsListData?.secondaryPartyDtl?.partyId,
            });
          }
          // for mdms user data
          let mdmsUserData = [
            {
              name: mdmsListData?.primaryPartyDtl?.fullName,
              phone: formatPhoneNumberForMDMS(mdmsListData?.primaryPartyDtl?.phoneNumber),
              email: mdmsListData?.primaryPartyDtl?.email,
              pinLevel: mdmsListData?.pin,
              directLeaderName: mdmsListData?.sponsor?.sponsorName,
              directLeaderPinLevel: mdmsListData?.sponsor?.pin,
              directLeaderId: mdmsListData?.sponsor?.sponsorABONo,
              gender: mdmsListData?.primaryPartyDtl?.gender?.toUpperCase(),
              race: mdmsListData?.primaryPartyDtl?.race?.toUpperCase(),
              address: mdmsListData?.primaryPartyDtl?.address,
              loaName: mdmsListData?.loaName,
              accountType: mdmsListData?.accountType,
              highestPinLevel: mdmsListData?.highestPinLevel,
              partyId: mdmsListData?.primaryPartyDtl?.partyId,
            },
          ];
          // for mdms couple user data
          if (mdmsListData?.isCouple) {
            mdmsUserData.push({
              name: mdmsListData?.secondaryPartyDtl?.fullName,
              phone: formatPhoneNumberForMDMS(mdmsListData?.secondaryPartyDtl?.phoneNumber),
              email: mdmsListData?.secondaryPartyDtl?.email,
              pinLevel: mdmsListData?.pin,
              directLeaderName: mdmsListData?.sponsor?.sponsorName,
              directLeaderPinLevel: mdmsListData?.sponsor?.pin,
              directLeaderId: mdmsListData?.sponsor?.sponsorABONo,
              gender: mdmsListData?.secondaryPartyDtl?.gender?.toUpperCase(),
              race: mdmsListData?.secondaryPartyDtl?.race?.toUpperCase(),
              address: mdmsListData?.secondaryPartyDtl?.address,
              loaName: mdmsListData?.loaName,
              accountType: mdmsListData?.accountType,
              highestPinLevel: mdmsListData?.highestPinLevel,
              partyId: mdmsListData?.secondaryPartyDtl?.partyId,
            });
          }

          setCoachData((prevData: CoachData | null) => {
            return {
              ...prevData!,
              name: mdmsUserData?.length === 1 ? mdmsUserData?.[0]?.name : '',
              contact: mdmsUserData?.length === 1 ? mdmsUserData?.[0]?.phone : '',
              email: mdmsUserData?.length === 1 ? mdmsUserData?.[0]?.email : '',
              aboID: aboNumber,
              pinLevel: mdmsUserData?.length === 1 ? mdmsUserData?.[0]?.pinLevel : '',
              directLeaderId: mdmsUserData?.length === 1 ? mdmsUserData?.[0]?.directLeaderId : '',
              directLeaderName: mdmsUserData?.length === 1 ? mdmsUserData?.[0]?.directLeaderName : '',
              directLeaderPinLevel: mdmsUserData?.length === 1 ? mdmsUserData?.[0]?.directLeaderPinLevel : '',
              gender: mdmsUserData?.length === 1 ? mdmsUserData?.[0]?.gender : '',
              race: mdmsUserData?.length === 1 ? mdmsUserData?.[0]?.race : '',
              address: mdmsUserData?.length === 1 ? mdmsUserData?.[0]?.address : '',
              loaName: mdmsUserData?.length === 1 ? mdmsUserData?.[0]?.loaName : '',
              accountType: mdmsUserData?.length === 1 ? mdmsUserData?.[0]?.accountType : '',
              highestPinLevel: mdmsUserData?.length === 1 ? mdmsUserData?.[0]?.highestPinLevel : '',
              partyId: mdmsUserData?.length === 1 ? mdmsUserData?.[0]?.partyId : '',
            };
          });

          setMDMSDataUsed(true);
          setUserList(mdmsUserList);
          setUserData(mdmsUserData);
        } else {
          setMDMSDataUsed(false);
          try {
            const result = await apiCall(userService.listUser);

            const retrieveUser = result?.data?.data.map((user: DynamicObjectInterface) => ({
              id: user?.loginId,
              title: user?.name,
            }));
            const filteredUsers = retrieveUser?.filter((user: DropdownOption) =>
              user.id.toLowerCase().includes(aboNumber?.toLowerCase()),
            );

            setUserList(filteredUsers);
          } catch (error) {
            LoggerService.log('Failed to retrieve campaign list', error);
          }
        }
      } catch (error) {
        LoggerService.log(error);
      }
    }
    setSelectedUserId('');
    setSelectedUserIndex(0);
  };

  const selectedUser = async (e: DynamicObjectInterface) => {
    try {
      const result = await apiCall(ParticipantService.searchParticipant, `${e?.id}`);
      const coachData = result?.data?.data[selectedUserIndex];
      const userData = result?.data?.data;
      setUserData(userData);
      setCoachData((prevCoachData: CoachData | null) => {
        if (!prevCoachData) return null;
        return {
          ...prevCoachData,
          name: e.title ?? coachData?.name,
          contact: e.phone ?? coachData?.phone ?? '',
          email: e.email ?? coachData?.email,
          pinLevel: e.pinLevel ?? coachData?.pinLevel,
          directLeaderId: e.directLeaderId ?? coachData?.directLeaderId,
          directLeaderName: e.directLeaderName ?? coachData?.directLeaderName,
          directLeaderPinLevel: e.directLeaderPinLevel ?? coachData?.directLeaderPin,
          gender: e.gender ?? coachData?.gender,
          race: e.race ?? coachData?.race,
          address: e.address ?? coachData?.address,
          loaName: e.loaName ?? coachData?.loaName,
          accountType: e.accountType ?? coachData?.accountType,
          highestPinLevel: e.highestPinLevel ?? coachData?.highestPinLevel,
          partyId: e.partyId ?? coachData?.partyId,
        };
      });
    } catch (error) {
      LoggerService.log('Failed to retrieve user details', error);
    }
  };

  return (
    <div>
      <AdminPageHeader
        title={isNewCoach ? t('admin.coachEdit.newTitle') : t('admin.coachEdit.editTitle')}
        button={buttonConfig}
      />
      <div className={styles['edit-coach-form-container']}>
        <div className={styles['input-row']}>
          <div className={styles['input-container1']}>
            <DropDown
              name="campaignId"
              dropdownLabel={`${t('admin.coachEnrollmentMaster.campaignId')}*`}
              disabledFilter={false}
              mlpVersion={MLPVersion.ONE}
              value={coachData?.campaignId}
              data={campaignDropdownData}
              configData={{
                label: 'title',
                onSelect: (selectedOption: DropDownData) => {
                  handleInputChange('campaignId', selectedOption.value);
                },
              }}
              // disabled={!isNewCoach}
            />
          </div>
        </div>
        <div className={styles['input-row']}>
          <div className={styles['input-container1']}>
            <Input
              key={id}
              disabled={!isNewCoach}
              type={InputTypes.TEXT}
              name={'coachID'}
              label={`${t('admin.coachMaster.coachID')}*`}
              value={coachData?.coachID}
              mlpVersion={MLPVersion.ONE}
              onChange={e => {
                handleInputChange('coachID', e.target.value);
              }}
              className={isNewCoach ? styles['highlighted-input'] : ''}
              status={coachData?.coachID === '' ? InputValidations.INVALID : InputValidations.DEFAULT}
              message={coachData?.coachID === '' ? t('admin.coachEdit.requiredCoachId') : ''}
            />
          </div>
          {!isNewCoach ? (
            <div className={styles['input-container']}>
              <div className={styles['input']}>
                <div className={styles['input-label']}>{`${t('admin.coachMaster.status')}*`}</div>
                <div className={styles['radio-button-container']}>
                  <RadioButton
                    label="Active"
                    checked={status === 'ACTIVE'}
                    onClick={() => {
                      setStatus(Status.ACTIVE);
                      handleInputChange('status', Status.ACTIVE);
                    }}
                  />
                  <RadioButton
                    label="Inactive"
                    checked={status === 'INACTIVE'}
                    onClick={() => {
                      setStatus(Status.INACTIVE);
                      handleInputChange('status', Status.INACTIVE);
                    }}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
        <div className={styles['input-row']}>
          <div className={styles['input-container']}>
            <SearchComponent
              label={`${t('admin.addParticipant.aboId')}*`}
              placeholder={t('admin.addParticipant.aboDescription')}
              initialSearchText={coachData?.aboID ?? ''}
              labelChoice={'id'}
              dropdownOptions={userList}
              mlpVersion={MLPVersion.TWO}
              onSearch={searchUser}
              status={coachData?.aboID === '' ? InputValidations.INVALID : InputValidations.DEFAULT}
              theme={coachData?.aboID === '' ? Theme.ERROR : Theme.BLACK}
              message={coachData?.aboID === '' ? t('admin.coachEdit.requiredAboId') : ''}
              onClear={() => {
                searchUser('');
                setCoachData({
                  recId: coachData?.recId ?? 0,
                  coachID: coachData?.coachID ?? '',
                  aboID: coachData?.aboID ?? '',
                  name: '',
                  contact: '(+65) ',
                  email: '',
                  remark: coachData?.remark ?? '',
                  status: coachData?.status ?? '',
                });
                setSelectedUserIndex(0);
                if (!mdmsDataUsed) {
                  selectedUser('');
                }
              }}
              onSelect={e => {
                handleInputChange('aboID', e.id);
                if (!mdmsDataUsed) {
                  selectedUser(e);
                }
              }}
              disabled={!isNewCoach}
            />
          </div>

          <div className={styles['input-container']}>
            {userData?.length > 1 ? (
              <DropDown
                name="name"
                dropdownLabel={`${t('admin.coachMaster.name')}*`}
                placeholder={t('admin.coachMaster.name')}
                mlpVersion={MLPVersion.ONE}
                value={selectedUserId}
                data={userData?.map(user => ({ id: user.name, title: user.name }))}
                configData={{
                  label: 'title',
                  onSelect: (selectedOption: DropDownData) => {
                    setSelectedUserId(selectedOption.title as string);
                    const selectedIndex = userData.findIndex(user => user.name === selectedOption.title);
                    setSelectedUserIndex(selectedIndex);
                    handleInputChange('name', userData[selectedIndex]?.name?.toString());
                    handleInputChange('contact', userData[selectedIndex]?.phone?.toString());
                    handleInputChange('email', userData[selectedIndex]?.email?.toString());
                    handleInputChange('pinLevel', userData[selectedIndex]?.pinLevel?.toString());
                    handleInputChange('directLeaderId', userData[selectedIndex]?.directLeaderId?.toString());
                    handleInputChange('directLeaderName', userData[selectedIndex]?.directLeaderName?.toString());
                    handleInputChange(
                      'directLeaderPinLevel',
                      userData[selectedIndex]?.directLeaderPinLevel?.toString(),
                    );
                    handleInputChange('gender', userData[selectedIndex]?.gender?.toString());
                    handleInputChange('race', userData[selectedIndex]?.race?.toString());
                    handleInputChange('address', userData[selectedIndex]?.address?.toString());
                    handleInputChange('loaName', userData[selectedIndex]?.loaName?.toString());
                    handleInputChange('accountType', userData[selectedIndex]?.accountType?.toString());
                    handleInputChange('highestPinLevel', userData[selectedIndex]?.highestPinLevel?.toString());
                    handleInputChange('partyId', userData[selectedIndex]?.partyId?.toString());
                  },
                }}
              />
            ) : (
              <Input
                key={id}
                type={InputTypes.TEXT}
                name={'name'}
                disabled
                label={`${t('admin.coachMaster.name')}*`}
                value={coachData?.name}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChange('name', e.target.value);
                }}
                status={coachData?.name === '' ? InputValidations.INVALID : InputValidations.DEFAULT}
                message={coachData?.name === '' ? t('admin.coachEdit.requiredName') : ''}
              />
            )}
          </div>
        </div>
        <div className={styles['input-row']}>
          <div className={styles['input-container']}>
            <div className={styles['label-text-phone']}>{t('admin.coachMaster.contact')}*</div>
            <PhoneNumber
              name={'phoneNumber'}
              data={countryCodeNumber}
              inputValue={phoneValue}
              //disabled
              dropdownDisabled
              dropdownValue={dropdownValue}
              status={phoneClicked ? InputValidations.INVALID : InputValidations.DEFAULT}
              message={phoneClicked ? t('admin.coachEdit.requiredPhone') : ''}
              configData={{
                label: 'name',
                onSelect: (e: DropDownData) => {
                  const combinedValue = `${e.name as string} ${phoneValue}`;
                  handleInputChange('contact', combinedValue);
                  setDropdownValue(e.name as string);
                },
              }}
              onInputChange={val => {
                const phoneNumber = val ?? '';
                setPhoneValue(phoneNumber);
                const combinedValue = `${dropdownValue} ${phoneNumber}`;
                setPhoneClicked(phoneNumber === '' || phoneNumber?.length < 8 || phoneNumber?.length > 10);
                handleInputChange('contact', combinedValue);
              }}
              onClick={() => setPhoneClicked(phoneValue === '')}
            />
          </div>
          <div className={styles['input-container']}>
            <Input
              key={id}
              type={InputTypes.TEXT}
              //disabled
              name={'email'}
              label={`${t('admin.coachMaster.email')}*`}
              value={coachData?.email}
              mlpVersion={MLPVersion.ONE}
              onChange={e => {
                handleInputChange('email', e.target.value);
              }}
              status={coachData?.email === '' || !emailValid ? InputValidations.INVALID : InputValidations.DEFAULT}
              message={
                coachData?.email === ''
                  ? t('admin.coachEdit.requiredEmail')
                  : emailValid
                    ? ''
                    : t('admin.coachEdit.invalidEmailFormat')
              }
            />
          </div>
        </div>
        <div className={styles['input-row']}>
          <div className={styles['input-container']}>
            <Input
              key={id}
              type={InputTypes.TEXT}
              name={'remark'}
              label={`${t('admin.coachMaster.remark')}`}
              value={coachData?.remark}
              mlpVersion={MLPVersion.ONE}
              onChange={e => {
                handleInputChange('remark', e.target.value);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
